import { createRoot } from 'react-dom/client';
import type { Permission, SwarmType } from '@/graphql/types';
import { overrides$, setGraphLoadedFlag } from './graph/graph-helpers';
import globalProfile, { ProfileProvider } from './profile';
import {
    applyReportPageProfiles,
    parsePageSettings,
} from './components/pages/reportBuilder/pageSettings';
import { Template } from './components/pages/reportBuilder/Template';
import {
    createRenderRuntime,
    reportElementClasses,
} from './components/pages/reportBuilder/elements/registry';
import { parseDefinitions } from './components/pages/reportBuilder/runtime/definitionParsing';
import { createRuntimeDataServices } from './components/pages/reportBuilder/runtime/dataServices/createRuntimeDataServices';
import { normalizeMeasuringPointModel } from './components/pages/reportBuilder/runtime/dataServices/projectMeasuringPoints/service';
import { isRecord } from './components/pages/reportBuilder/runtime/dataServices/validationPrimitives';
import { createDeferred } from './utils/deferred';
import { getClient } from './utils/apollo-client';
import warning from './utils/logger';
import type { Graph } from './graph/graph';
import type { Profile } from './profile';
import type { PageSettings } from './components/pages/reportBuilder/pageSettings';
import type {
    ElementDefinition,
    RenderContext,
    RenderRuntimeGraphSettings,
} from './components/pages/reportBuilder/elements/types';

declare global {
    interface Window {
        graphs?: Graph[];
        // Read by utils/pdf.py when window.status is graph_error.
        graphError?: string;
    }
}

interface CustomTemplateMeasuringPointInput {
    id: string;
    name: string;
    deviceName?: string | null;
    swarmType: SwarmType;
    permission: Permission;
}

interface CustomTemplateProjectInput {
    id: string;
    name: string;
    timezone: string;
}

type CustomTemplateContextInput =
    | {
          project: CustomTemplateProjectInput;
          measuringPoint?: never;
      }
    | {
          project?: never;
          measuringPoint: CustomTemplateMeasuringPointInput;
      };

type CustomTemplateModuleOptions = PageSettings &
    RenderRuntimeGraphSettings &
    CustomTemplateContextInput & {
        structure: unknown;
        overrides?: Record<string, unknown>;
        profile?: Profile;
    };

interface CustomTemplateModule {
    options: CustomTemplateModuleOptions;
}

function hasProjectContext(
    moduleOptions: CustomTemplateModuleOptions
): moduleOptions is CustomTemplateModuleOptions & { project: CustomTemplateProjectInput } {
    return typeof moduleOptions.project !== 'undefined';
}

function createCustomTemplateRenderContext(
    moduleOptions: CustomTemplateModuleOptions
): RenderContext {
    if (hasProjectContext(moduleOptions)) {
        const { id, name, timezone } = moduleOptions.project;

        if (id === null || typeof id === 'undefined') {
            throw new Error('Missing "module.options.project.id" for custom template runtime.');
        }

        if (typeof name !== 'string') {
            throw new Error('Expected "module.options.project.name" to be a string.');
        }

        if (typeof timezone !== 'string' || timezone.trim().length === 0) {
            throw new Error('Expected "module.options.project.timezone" to be a string.');
        }

        return {
            kind: 'project',
            project: {
                id,
                name,
                timezone,
            },
        };
    }

    return {
        kind: 'measuring-point',
        measuringPoint: normalizeMeasuringPointModel(
            moduleOptions.measuringPoint,
            'module.options.measuringPoint'
        ),
    };
}

function createCustomTemplateGraphSettings(
    moduleOptions: CustomTemplateModuleOptions
): RenderRuntimeGraphSettings {
    return {
        viewStartTs: moduleOptions.viewStartTs,
        viewEndTs: moduleOptions.viewEndTs,
        preferredGraphType: moduleOptions.preferredGraphType,
        frequencyGraphScaleType: moduleOptions.frequencyGraphScaleType,
        dataType: moduleOptions.dataType,
        valueScale: moduleOptions.valueScale,
        valueScaleEquation: moduleOptions.valueScaleEquation,
        frequencyScale: moduleOptions.frequencyScale,
        hiddenLines: moduleOptions.hiddenLines,
        getAlarmsUrl: (measuringPointId) => moduleOptions.getAlarmsUrl(measuringPointId),
    };
}

function normalizeOverrides(overrides: unknown): Record<string, unknown> {
    if (typeof overrides === 'undefined') {
        return {};
    }

    if (!isRecord(overrides)) {
        throw new Error('Expected "module.options.overrides" to be an object when provided.');
    }

    return overrides;
}

async function mountCustomTemplateTree(
    moduleOptions: CustomTemplateModuleOptions,
    structure: ElementDefinition[],
    containerDiv: HTMLDivElement
): Promise<void> {
    const context = createCustomTemplateRenderContext(moduleOptions);
    const profile = moduleOptions.profile || globalProfile;
    const pageSettings = parsePageSettings(moduleOptions, 'module.options');
    const runtime = createRenderRuntime(
        reportElementClasses,
        false,
        pageSettings,
        createCustomTemplateGraphSettings(moduleOptions),
        createRuntimeDataServices(getClient()),
        'export',
        profile
    );
    const reactRoot = createRoot(containerDiv);
    const treeReady = createDeferred<void>();

    reactRoot.render(
        <ProfileProvider profile={profile}>
            <Template
                structure={structure}
                context={context}
                runtime={runtime}
                onReady={(readyRuntime) => {
                    applyReportPageProfiles({
                        defaultProfile: pageSettings,
                        profiles: readyRuntime.getPageProfiles(),
                    });

                    const graphs = readyRuntime.getRenderedGraphs();
                    window.graphs = graphs;
                    treeReady.resolve(undefined);
                }}
                onError={(error) => {
                    treeReady.reject(error);
                }}
            />
        </ProfileProvider>
    );

    await setGraphLoadedFlag(treeReady.promise);
}

export async function reportExportPageForCustomTemplate(
    module: CustomTemplateModule
): Promise<void> {
    try {
        const containerDiv = document.createElement('div');
        document.body.appendChild(containerDiv);

        const structure = parseDefinitions(module.options.structure, ['custom_template']);
        overrides$.next(normalizeOverrides(module.options.overrides));

        await mountCustomTemplateTree(module.options, structure, containerDiv);
    } catch (error: unknown) {
        // Set the reason before signalling failure to the PDF renderer.
        window.graphError = error instanceof Error ? error.message : String(error);
        window.status = 'graph_error';
        warning(error instanceof Error ? error : String(error));
    }
}
